import { useEffect, useState } from "react";

/**
 * PageSkeleton - Generic skeleton loader for page content
 * Displays the Becoming Alpha logo centered on screen while loading
 */
export default function PageSkeleton() {
  const [isVisible, setIsVisible] = useState(false);

  // Scroll to top when skeleton loads
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger fade-in after mount
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-[#09091e] z-50">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 211.74 158.62"
        className={`w-[300px] h-auto max-w-[90vw] animate-pulse transition-opacity duration-1000 ease-in-out ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        aria-label="Loading"
        role="img"
      >
        <defs>
          <style>
            {`.skeleton-logo-fill-none { fill: none; }
              .skeleton-logo-fill-white { fill: #fff; }`}
          </style>
        </defs>
        <g>
          <path
            className="skeleton-logo-fill-white"
            d="M10.94,158.09H.24v-5.5c0-1.06.25-1.9.75-2.52.24-.3.55-.52.92-.68.37-.16.77-.24,1.18-.24.48,0,.91.11,1.3.34.39.22.67.55.84.98h.03c.06-.62.34-1.11.83-1.45s1.07-.52,1.73-.52c.45,0,.88.08,1.28.25.41.17.74.42,1.01.76.54.67.81,1.63.81,2.89v5.7ZM4.8,152.44c0-.29-.04-.57-.11-.86-.08-.29-.2-.52-.36-.72-.14-.15-.32-.27-.54-.36-.22-.09-.46-.13-.71-.13s-.49.04-.72.13c-.22.09-.41.21-.55.36-.34.38-.5.95-.5,1.71v4.26h3.5v-4.4ZM9.89,152.39c0-.97-.19-1.67-.58-2.11-.15-.18-.36-.32-.61-.41-.25-.09-.52-.14-.81-.14s-.58.05-.84.15-.48.25-.64.46c-.21.25-.36.56-.44.9-.08.35-.11.73-.11,1.15v4.45h4.03v-4.45Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.94,128.35v8.28H.24v-8.28h1.05v7.01h3.77v-6.74h1.05v6.74h3.74v-7.01h1.07Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.45,114.2c-.48.86-1.15,1.53-2.01,2.01-.86.48-1.81.73-2.87.73s-2.01-.24-2.86-.73c-.85-.48-1.51-1.15-1.99-2.01-.48-.86-.72-1.82-.72-2.9s.25-2.11.76-3.03,1.25-1.55,2.24-1.91l.24,1.08c-.75.34-1.3.85-1.65,1.54-.35.69-.52,1.46-.52,2.32s.19,1.6.57,2.26.91,1.18,1.59,1.54c.68.37,1.46.55,2.34.55s1.66-.18,2.35-.55c.69-.37,1.22-.88,1.6-1.54.38-.66.57-1.42.57-2.26s-.19-1.57-.57-2.29c-.38-.72-.92-1.24-1.64-1.54l.24-1.1c1.01.41,1.77,1.06,2.28,1.96.51.9.76,1.89.76,2.97s-.24,2.05-.73,2.9Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.4,92.6c-.47.86-1.13,1.53-1.98,2.01-.85.48-1.8.73-2.87.73s-2.01-.24-2.86-.73c-.85-.48-1.51-1.15-1.98-2.01-.47-.86-.71-1.82-.71-2.9s.24-2.05.71-2.9c.47-.86,1.13-1.53,1.98-2.01s1.8-.73,2.86-.73,2.03.24,2.87.73,1.51,1.15,1.98,2.01c.47.86.71,1.82.71,2.9s-.24,2.05-.71,2.9ZM9.47,87.44c-.38-.66-.91-1.18-1.6-1.55-.68-.37-1.46-.56-2.32-.56s-1.64.19-2.32.56c-.68.37-1.21.89-1.59,1.55s-.57,1.42-.57,2.26.19,1.62.57,2.28.9,1.18,1.58,1.54c.68.37,1.45.55,2.33.55s1.65-.18,2.33-.55c.68-.37,1.21-.88,1.59-1.55.38-.67.57-1.42.57-2.27s-.19-1.6-.57-2.26Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.94,70.84v1.25H.24v-1.33l6.27-4.94L.24,60.89v-1.33h10.7v1.27H2.03l5.82,4.63v.75l-5.82,4.63h8.91Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.94,45.75v1.25H.24v-1.25h10.7Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.94,31.95v1.25H.24v-1.28l8.97-7.52H.24v-1.25h10.7v1.28L1.97,31.95h8.97Z"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M10.45,8.52c-.47.86-1.13,1.53-1.98,2.02s-1.8.73-2.86.73-2.03-.24-2.88-.73-1.52-1.16-2-2.02c-.48-.86-.73-1.83-.73-2.91s.25-2.09.76-2.99c.51-.9,1.26-1.53,2.25-1.89l.23,1.09c-.75.35-1.3.85-1.64,1.52s-.51,1.42-.51,2.27.19,1.6.57,2.27c.38.67.92,1.18,1.6,1.55.69.37,1.47.55,2.35.55s1.65-.18,2.33-.55c.68-.37,1.2-.88,1.58-1.54.38-.66.57-1.42.57-2.28s-.16-1.61-.47-2.32c-.31-.71-.81-1.21-1.51-1.52h-1.82v4h-1.05V.66h3.09c.98.4,1.69,1.04,2.15,1.93.45.89.68,1.9.68,3.02s-.24,2.05-.71,2.91Z"
          />
        </g>
        <path
          className="skeleton-logo-fill-none"
          d="M139.24,2.97v69.03h69.18c-.73-31.91-24.96-68.38-69.18-69.03Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M145.78,74.96h-6.54v6.52c1.61-2.58,3.86-4.85,6.54-6.52Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M111.47,2.97H22.68v87.66C32.98,44.83,68.93,12.55,111.47,2.97Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M139.51,103.05v13.26h13.97c-5.55-2.65-10.83-7.08-13.97-13.26Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M22.69,116.3h113.59V2.98C77.16,3.86,23.47,49.25,22.69,116.3Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M164.04,116.24v-24.41h-24.48c.83,15.42,14.54,23.65,24.48,24.41Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M208.44,53.15V2.97h-48.89c25.85,7.49,42.49,28.17,48.89,50.18Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M182.72,116.3h25.71v-26.16c-4.25,11.54-12.99,21.44-25.71,26.16Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M161.25,76.63c-1.21-1.01-2.95-1.67-5.41-1.67-8.29,0-15.36,6.73-16.23,13.92h19.48c4.08,0,4.96-3.97,4.96-4.97v-.22c0-1.15-.14-4.85-2.79-7.06Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M164.04,75.21v-.25h-.23c.08.08.16.16.23.25Z"
        />
        <path
          className="skeleton-logo-fill-none"
          d="M208.41,74.96h-41.41v41.32c26.04-.73,40.74-20.8,41.41-41.32Z"
        />
        <path
          className="skeleton-logo-fill-white"
          d="M209.92,0H21.2c-.82,0-1.48.66-1.48,1.48v116.29c0,.82.66,1.48,1.48,1.48h116.56s.09,0,.14,0c.04,0,.09,0,.14,0h71.89c.82,0,1.48-.66,1.48-1.48V1.49c0-.82-.66-1.48-1.48-1.48ZM22.68,2.97h88.79C68.93,12.55,32.98,44.83,22.68,90.62V2.97ZM136.28,116.3H22.69C23.47,49.25,77.16,3.86,136.28,2.98v113.32ZM139.24,2.97c44.22.65,68.45,37.11,69.18,69.03h-69.18V2.97ZM139.24,74.96h6.54c-2.67,1.67-4.93,3.95-6.54,6.52v-6.52ZM139.51,116.3v-13.26c3.15,6.17,8.43,10.6,13.97,13.26h-13.97ZM164.04,116.24c-9.94-.76-23.65-8.99-24.48-24.41h24.48v24.41ZM164.04,83.91c0,.99-.88,4.97-4.96,4.97h-19.48c.87-7.19,7.94-13.92,16.23-13.92,2.47,0,4.2.66,5.41,1.67,2.65,2.21,2.78,5.92,2.79,7.06v.22ZM164.04,75.21c-.08-.08-.15-.17-.23-.25h.23v.25ZM167,74.96h41.41c-.68,20.52-15.38,40.59-41.41,41.32v-41.32ZM208.44,116.3h-25.71c12.72-4.72,21.46-14.63,25.71-26.16v26.16ZM208.44,53.15c-6.4-22.01-23.04-42.7-48.89-50.18h48.89v50.18Z"
        />
        <rect className="skeleton-logo-fill-none" x="19.54" width="192" height="119.25" />
        <g>
          <path
            className="skeleton-logo-fill-white"
            d="M19.5,125.74v32.88h192.24v-32.88H19.5ZM31.33,150.9l9.42-18.58h3.19l9.37,18.58h-4.04l-2.27-4.43h-9.47l-2.27,4.43h-3.93ZM86.1,147.73v3.17h-14.75v-18.47h3.72v15.3h11.03ZM104.94,150.9v-18.47h9.1c2.23,0,4.01.53,5.34,1.6,1.33,1.06,1.99,2.63,1.99,4.71,0,1.42-.32,2.62-.97,3.59-.65.97-1.54,1.68-2.65,2.15s-2.37.7-3.76.7h-5.41v5.73h-3.64ZM154.14,140.08v-7.65h3.67v18.47h-3.67v-7.65h-9.95v7.65h-3.69v-18.47h3.69v7.65h9.95ZM193.87,150.9l-2.27-4.43h-9.47l-2.27,4.43h-3.93l9.42-18.58h3.19l9.37,18.58h-4.04ZM211.59,158.42h-.03v-32.68h.03v32.68Z"
          />
          <polygon
            className="skeleton-logo-fill-white"
            points="43.63 139.42 42.36 136.02 40.99 139.47 39.06 143.4 45.47 143.4 43.63 139.42"
          />
          <path
            className="skeleton-logo-fill-white"
            d="M117.79,138.73c0-2.09-1.25-3.14-3.75-3.14h-5.46v6.52h5.44c2.52-.04,3.77-1.16,3.77-3.38Z"
          />
          <polygon
            className="skeleton-logo-fill-white"
            points="186.96 136.02 185.59 139.47 183.66 143.4 190.07 143.4 188.23 139.42 186.96 136.02"
          />
        </g>
      </svg>
    </div>
  );
}
